import { useState } from 'react';
import { useInvoices } from '../hooks/useInvoices';
import { paymentsService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/atoms/Icon';
import toast from 'react-hot-toast';
import { formatCurrency, getBranchName } from '../utils/constants';

const Dashboard = () => {
  const { user } = useAuth();
  const {
    facturas,
    resumen,
    selectedFacturas,
    loading,
    error,
    loadPendingInvoices,
    updateSummary,
    toggleFactura,
    selectAllFacturas,
    clearSelection,
    isFacturaSelected,
    validateSelection,
    hasFacturas,
    hasSelection,
    selectedCount,
    totalSelected,
  } = useInvoices();

  const [processingPayment, setProcessingPayment] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [paymentEmail, setPaymentEmail] = useState('');
  const [tucompraPayload, setTucompraPayload] = useState(null);
  const [tucompraGatewayUrl, setTucompraGatewayUrl] = useState('');

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    const date = new Date(iso);
    return date.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const handleSelectAll = () => {
    if (selectedCount === facturas.length) {
      clearSelection();
    } else {
      selectAllFacturas();
    }
  };

  const handleProceedPayment = () => {
    if (validateSelection()) {
      setShowConfirmation(true);
    }
  };

  const submitToTuCompra = () => {
    if (!tucompraPayload || !tucompraGatewayUrl) {
      toast.error('No hay datos para enviar a la pasarela');
      return;
    }

    const emailFinal = (paymentEmail || '').trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailFinal);

    if (!emailOk) {
      toast.error('Ingresa un correo válido');
      return;
    }

    const payloadFinal = {
      ...tucompraPayload,
      correoComprador: emailFinal,
    };

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = tucompraGatewayUrl;
    form.style.display = 'none';

    Object.entries(payloadFinal).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value ?? '';
      form.appendChild(input);
    });

    document.body.appendChild(form);

    toast.success('Redirigiendo a la pasarela de pagos...', { duration: 2000, icon: '🔄' });

    setShowEmailModal(false);
    setTucompraPayload(null);
    setTucompraGatewayUrl('');
    setPaymentEmail('');

    setTimeout(() => form.submit(), 700);
  };

  const handleConfirmPayment = async () => {
    setProcessingPayment(true);
    try {
      const paymentData = { facturas: selectedFacturas, valorTotal: totalSelected };
      const response = await paymentsService.create(paymentData);

      if (response.data.success) {
        const { tucompraData, gatewayUrl } = response.data.data;

        setTucompraPayload(tucompraData);
        setTucompraGatewayUrl(gatewayUrl);
        setPaymentEmail(tucompraData?.correoComprador || '');

        setShowConfirmation(false);
        setShowEmailModal(true);

        toast.success('Confirma el correo para el pago', { duration: 2000, icon: '✉️' });
      } else {
        toast.error('Error creando referencia de pago');
      }
    } catch (err) {
      console.error('Error procesando pago:', err);
      toast.error(err.response?.data?.message || 'Error procesando el pago');
    } finally {
      setProcessingPayment(false);
    }
  };

  const BalanceSummary = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="mb-4 sm:mb-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">TRM Hoy</p>
          <p className="text-2xl font-bold text-ec-primary-600">${resumen.trm.toLocaleString('es-CO')}</p>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 text-sm font-semibold disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 border-ec-primary-500 text-ec-primary-600 hover:bg-ec-primary-500 hover:text-white"
          onClick={updateSummary}
          disabled={loading}
        >
          <Icon name="refresh-cw" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Actualizar</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Saldo Corriente */}
        <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex-shrink-0 bg-green-100 p-2 rounded-full"><Icon name="arrow-right-circle" className="h-5 w-5 text-green-600" /></div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Saldo Corriente</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(resumen.saldoCorriente)}</p>
          </div>
        </div>
        {/* Saldo Vencido */}
        <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex-shrink-0 bg-red-100 p-2 rounded-full"><Icon name="alert-triangle" className="h-5 w-5 text-red-600" /></div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Saldo Vencido</p>
            <p className="text-xl font-bold text-red-700">{formatCurrency(resumen.saldoVencido)}</p>
          </div>
        </div>
        {/* Saldo Total */}
        <div className="flex items-start gap-4 p-4 bg-ec-primary-50 rounded-lg border border-ec-primary-200">
          <div className="flex-shrink-0 bg-ec-primary-100 p-2 rounded-full"><Icon name="dollar-sign" className="h-5 w-5 text-ec-primary-600" /></div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Saldo Total</p>
            <p className="text-xl font-bold text-ec-primary-700">{formatCurrency(resumen.saldoTotal)}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const InvoiceRow = ({ factura }) => (
    <tr
      className={`transition-colors cursor-pointer ${isFacturaSelected(factura) ? 'bg-ec-primary-50' : 'hover:bg-gray-50'} ${factura.vencida && !isFacturaSelected(factura) ? 'bg-red-50/50' : ''}`}
      onClick={() => toggleFactura(factura)}
    >
      <td className="px-4 sm:px-6 py-4">
        <input
          type="checkbox"
          checked={isFacturaSelected(factura)}
          onChange={() => { }}
          className="h-4 w-4 text-ec-primary-600 focus:ring-ec-primary-500 border-gray-300 rounded cursor-pointer"
        />
      </td>
      <td className="px-4 sm:px-6 py-4 font-semibold text-gray-900">{factura.numero}</td>
      <td className="px-4 sm:px-6 py-4">
        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{getBranchName(factura.sucursal)}</span>
      </td>
      <td className="px-4 sm:px-6 py-4 text-sm text-gray-600">{formatDate(factura.fechaVencimiento)}</td>
      <td className="px-4 sm:px-6 py-4">
        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${factura.vencida ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {factura.diasVencimiento > 0 ? `+${factura.diasVencimiento}` : factura.diasVencimiento}
        </span>
      </td>
      <td className="px-4 sm:px-6 py-4 text-right font-semibold text-gray-900">{formatCurrency(factura.divisa === 'COP' ? factura.saldoCOP : factura.saldoOriginal, factura.divisa)}</td>
      <td className="px-4 sm:px-6 py-4">
        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{factura.divisa}</span>
      </td>
    </tr>
  );

  const InvoiceCard = ({ factura }) => (
    <div
      className={`rounded-lg border p-4 transition-all cursor-pointer ${isFacturaSelected(factura) ? 'bg-ec-primary-50 border-ec-primary-300 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300'} ${factura.vencida && !isFacturaSelected(factura) ? 'bg-red-50/50' : ''}`}
      onClick={() => toggleFactura(factura)}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="font-bold text-gray-900">{factura.numero}</p>
          <span className="inline-flex mt-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{getBranchName(factura.sucursal)}</span>
        </div>
        <input
          type="checkbox"
          checked={isFacturaSelected(factura)}
          onChange={() => { }}
          className="h-5 w-5 mt-1 text-ec-primary-600 focus:ring-ec-primary-500 border-gray-300 rounded cursor-pointer"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm border-t border-gray-200 pt-3">
        <div className="text-gray-500">Vencimiento:</div>
        <div className="text-gray-800 font-medium text-right">{formatDate(factura.fechaVencimiento)}</div>
        <div className="text-gray-500">Días vencida:</div>
        <div className="text-right">
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${factura.vencida ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
            {factura.diasVencimiento > 0 ? `+${factura.diasVencimiento}` : factura.diasVencimiento}
          </span>
        </div>
        <div className="text-gray-500 font-semibold">Saldo:</div>
        <div className="text-gray-900 font-bold text-right">{formatCurrency(factura.divisa === 'COP' ? factura.saldoCOP : factura.saldoOriginal, factura.divisa)}</div>
      </div>
    </div>
  );

  const InvoicesTable = () => {
    if (loading) return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <Icon name="loader" className="h-10 w-10 text-ec-primary-500 animate-spin mx-auto mb-4" />
        <p className="text-base font-semibold text-gray-700">Cargando facturas pendientes...</p>
      </div>
    );
    if (error) return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3 text-left">
          <Icon name="alert-circle" className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-red-800 text-sm">{error}</span>
        </div>
        <button
          className="inline-flex items-center gap-2 px-6 py-2 bg-ec-primary-600 text-white rounded-full font-medium hover:bg-ec-primary-700 transition-colors"
          onClick={loadPendingInvoices}
        >
          <Icon name="refresh-cw" className="h-4 w-4" />
          <span>Reintentar</span>
        </button>
      </div>
    );
    if (!hasFacturas) return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <Icon name="check-circle" className="h-14 w-14 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-800 mb-1">¡Todo al día!</h3>
        <p className="text-gray-600">No tiene facturas pendientes por pagar.</p>
      </div>
    );
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-800">Facturas Pendientes ({facturas.length})</h3>
          <button
            className="flex self-start sm:self-center items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50"
            onClick={handleSelectAll}
          >
            <Icon name={selectedCount === facturas.length ? 'check-square' : 'square'} className="h-4 w-4" />
            <span>{selectedCount === facturas.length ? 'Deseleccionar Todo' : 'Seleccionar Todo'}</span>
          </button>
        </div>

        <div className="md:hidden p-4 space-y-3 bg-gray-50/70">
          {facturas.map((factura) => <InvoiceCard key={`${factura.numero}-${factura.sucursal}`} factura={factura} />)}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sucursal</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vencimiento</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Días</th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Divisa</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {facturas.map((factura) => <InvoiceRow key={`${factura.numero}-${factura.sucursal}`} factura={factura} />)}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${hasSelection ? 'pb-40' : ''}`}>
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel de Pagos</h1>
              <p className="mt-1 text-gray-600">Gestiona tus facturas pendientes de forma segura.</p>
            </div>
            {user?.razonSocial && (
              <div className="text-left sm:text-right p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-semibold text-gray-800">{user.razonSocial}</p>
                {user?.nit && <p className="text-xs text-gray-500">NIT: {user.nit}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {resumen && <BalanceSummary />}
        <InvoicesTable />
      </div>

      {hasSelection && (
        <div className="sticky bottom-0 left-0 right-0 z-30">
          <div className="bg-white/80 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                <div className="flex w-full items-center justify-between md:w-auto md:justify-start md:gap-6">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Seleccionado</p>
                    <p className="text-lg font-semibold text-gray-800">{selectedCount} de {facturas.length}</p>
                  </div>
                  <div className="md:border-l md:border-gray-300 md:pl-6">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total a Pagar</p>
                    <p className="text-2xl font-extrabold text-ec-primary-700 text-right md:text-left">{formatCurrency(totalSelected)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                  <button
                    onClick={clearSelection}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-100 transition-colors text-sm"
                  >
                    Limpiar
                  </button>
                  <button
                    onClick={handleProceedPayment}
                    disabled={processingPayment}
                    className="flex-grow md:flex-grow-0 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white bg-gradient-to-r from-ec-primary-600 to-ec-primary-700 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:from-ec-primary-400 disabled:to-ec-primary-500 disabled:shadow-none disabled:translate-y-0 disabled:cursor-wait"
                  >
                    <Icon name={processingPayment ? "loader" : "credit-card"} className={`h-5 w-5 ${processingPayment ? "animate-spin" : ""}`} />
                    <span>{processingPayment ? "Procesando..." : "Pagar"}</span>
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ease-out" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !processingPayment && setShowConfirmation(false)}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transition-transform duration-300 ease-out" style={{ animation: 'scaleUp 0.3s ease-out' }}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Confirmar Pago</h3>
              <button onClick={() => !processingPayment && setShowConfirmation(false)} className="text-gray-400 hover:text-gray-600 transition-colors"><Icon name="x" className="h-6 w-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700">Está a punto de realizar un pago con los siguientes datos:</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Facturas a pagar:</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Valor total:</p>
                  <p className="text-lg font-semibold text-gray-800">{formatCurrency(totalSelected)}</p>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="alert-triangle" className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-800">Será redirigido a la plataforma de pagos segura para completar la transacción.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 p-5 bg-gray-50 border-t border-gray-200 rounded-b-xl">
              <button
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-100 transition-colors disabled:opacity-60"
                onClick={() => setShowConfirmation(false)}
                disabled={processingPayment}
              >
                Cancelar
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full font-semibold text-white bg-green-600 hover:bg-green-700 shadow-sm transition-all disabled:bg-green-400 disabled:cursor-wait"
                onClick={handleConfirmPayment}
                disabled={processingPayment}
              >
                <Icon name={processingPayment ? "loader" : "check-circle"} className={`h-5 w-5 ${processingPayment ? "animate-spin" : ""}`} />
                <span>{processingPayment ? "Procesando..." : "Confirmar y Pagar"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ease-out">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !processingPayment && setShowEmailModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Correo para el pago</h3>
              <button
                onClick={() => !processingPayment && setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="x" className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-gray-700 text-sm">
                Este correo se enviará a la pasarela (TuCompra) para la transacción.
              </p>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Correo</label>

                <div className="relative">
                  <input
                    type="email"
                    value={paymentEmail}
                    onChange={(e) => setPaymentEmail(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ec-primary-500"
                    placeholder="correo@dominio.com"
                  />

                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                    <Icon name="edit-3" className="h-4 w-4" />
                  </span>
                </div>

                <p className="text-xs text-gray-500">
                  Por defecto viene el correo del usuario registrado, pero aquí puedes cambiarlo para este pago.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Facturas a pagar:</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Valor total:</p>
                  <p className="text-lg font-semibold text-gray-800">{formatCurrency(totalSelected)}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="alert-triangle" className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  Esto no cambia el correo del perfil en el sistema; solo aplica para esta transacción.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 p-5 bg-gray-50 border-t border-gray-200 rounded-b-xl">
              <button
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-100 transition-colors disabled:opacity-60"
                onClick={() => setShowEmailModal(false)}
                disabled={processingPayment}
              >
                Volver
              </button>

              <button
                className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full font-semibold text-white bg-green-600 hover:bg-green-700 shadow-sm transition-all disabled:bg-green-400 disabled:cursor-wait"
                onClick={submitToTuCompra}
                disabled={processingPayment}
              >
                <Icon name={processingPayment ? "loader" : "check-circle"} className={`h-5 w-5 ${processingPayment ? "animate-spin" : ""}`} />
                <span>{processingPayment ? "Procesando..." : "Continuar a pagar"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleUp { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default Dashboard;